import React, { ElementRef, useLayoutEffect, useRef } from 'react';
import useResizeObserver from '@react-hook/resize-observer';

interface UseContainerQueryProps {
  minWidth?: number;
  maxWidth?: number;
}

type BorderBoxSize = ResizeObserverSize | ResizeObserverSize[] | undefined;

const isWidthInRange = (width: number, minWidth: number, maxWidth?: number) =>
  maxWidth === undefined ? width >= minWidth : width >= minWidth && width <= maxWidth;

const getBorderInlineSize = (borderBoxSize: BorderBoxSize) => {
  if (!borderBoxSize) return undefined;

  if (Array.isArray(borderBoxSize)) {
    return borderBoxSize[0]?.inlineSize;
  }

  return borderBoxSize.inlineSize;
};

const getEntryWidth = (entry: ResizeObserverEntry) =>
  getBorderInlineSize(entry.borderBoxSize as BorderBoxSize) ?? entry.contentRect.width;

const useWidthMatchUpdater = ({
  minWidth,
  maxWidth,
  setMatch,
  onMatchChange,
}: {
  minWidth: number;
  maxWidth?: number;
  setMatch: React.Dispatch<React.SetStateAction<boolean>>;
  onMatchChange?: (isMatch: boolean) => void;
}) =>
  React.useCallback(
    (width?: number) => {
      if (!width || width <= 0) return;

      const match = isWidthInRange(width, minWidth, maxWidth);
      setMatch(match);
      onMatchChange?.(match);
    },
    [maxWidth, minWidth, onMatchChange, setMatch]
  );

export function useContainerQuery<T extends React.ElementType = 'div'>({
  minWidth = 1,
  maxWidth,
  onMatchChange,
}: Readonly<UseContainerQueryProps & { onMatchChange?: (isMatch: boolean) => void }>) {
  const [isMatch, setIsMatch] = React.useState(false);
  const containerRef = useRef<ElementRef<T>>(null);
  const updateMatch = useWidthMatchUpdater({ minWidth, maxWidth, onMatchChange, setMatch: setIsMatch });

  useLayoutEffect(() => {
    const element = containerRef.current as unknown as HTMLElement | null;
    if (!element) return;

    updateMatch(element.getBoundingClientRect().width);
  }, [updateMatch]);

  useResizeObserver(containerRef as unknown as React.RefObject<HTMLDivElement>, (entry) => {
    updateMatch(getEntryWidth(entry));
  });

  return [isMatch, containerRef] as const;
}

export function useTargetElementContainerQuery({
  minWidth = 1,
  maxWidth,
  element,
}: {
  minWidth?: number;
  maxWidth?: number;
  element: HTMLElement | null;
}) {
  const [isMatch, setIsMatch] = React.useState(false);
  const updateMatch = useWidthMatchUpdater({ minWidth, maxWidth, setMatch: setIsMatch });

  useLayoutEffect(() => {
    if (!element) return;

    updateMatch(element.getBoundingClientRect().width);
  }, [element, updateMatch]);

  useResizeObserver(element, (entry) => {
    updateMatch(getEntryWidth(entry));
  });

  return [isMatch] as const;
}
